export const SYSTEM_ACTOR_TYPES = ["user", "ai"] as const
export type SystemActorType = (typeof SYSTEM_ACTOR_TYPES)[number]

export const aiActorTypeIds = { mareczekAi: "mareczek-ai" } as const
export type AiActorTypeId = (typeof aiActorTypeIds)[keyof typeof aiActorTypeIds]

export const AGENT_ACTION_STATUSES = [
	"pending",
	"accepted",
	"declined",
	"expired",
] as const
export type AgentActionStatus = (typeof AGENT_ACTION_STATUSES)[number]

/**
 * A mutation Mareczek proposed in chat, awaiting the owner's approval
 * (ZPL-122). Clients render per-tool localized copy from `toolName` +
 * `displayParams` (locale-neutral values: names, emails, amounts) and an
 * Accept/Decline pair while `status === "pending"`; resolved statuses
 * collapse to a status line. Unknown future `toolName`s must degrade to a
 * generic action bubble — never hide the message.
 */
export interface AgentActionContent {
	actionId: string
	toolName: string
	displayParams: Record<string, string>
	status: AgentActionStatus
	/**
	 * The only user who may act on the card (the private-chat owner / shared-
	 * chat mentioner). Clients render Accept/Decline ONLY for them — the
	 * server 404s everyone else anyway (ZPL-295). Optional: cards posted
	 * before this field shipped lack it; treat missing as actionable so a
	 * pending pre-deploy card doesn't lose its buttons for its owner.
	 */
	userId?: string
}

/**
 * An @-mention carried on a free-form chat message (shared chats only).
 * `handle` is the display token shown after the `@` (e.g. `patryk.wojtyński`),
 * snapshotted at send time so the bubble can highlight the span without a live
 * lookup; `userId` / the AI `actorId` are the durable references the
 * notification fan-out (realtime toast, FCM, email, Mareczek reply) consumes
 * in the follow-up sub-issues. The core just stores + renders them.
 */
export type ChatMention =
	| { type: "user"; userId: string; handle: string }
	| { type: "ai"; actorId: AiActorTypeId; handle: string }

/**
 * The @-mention display handle for a person's name: the WHOLE name lowercased
 * with whitespace runs collapsed to dots, diacritics preserved
 * (`Patryk Wojtyński` → `patryk.wojtyński`). This is a presentation transform
 * of the full name — it deliberately does NOT parse the name into components
 * (see docs/ai-guidelines/no-algorithmic-name-parsing.md); the mention's
 * durable reference stays the userId, the handle is display sugar only.
 */
export function mentionHandle(displayName: string): string {
	return displayName.trim().toLowerCase().replace(/\s+/g, ".")
}

/**
 * Persisted message body. Shapes are discriminated by the presence of
 * `message` (free-form text), `templateKey` (renderable in the viewer's
 * locale), or `agentAction` (approval bubble). Templates carry their
 * interpolation params alongside so the server doesn't need to know the
 * viewer's language at write time.
 *
 * Free-form messages may carry `mentions` (ZPL-145) — resolved @-mentions of
 * chat participants and/or Mareczek, server-validated against the thread.
 */
export type ChatMessageContent =
	| { message: string; mentions?: ChatMention[] }
	| { templateKey: string; templateParams: Record<string, string> }
	| { agentAction: AgentActionContent }

export interface ChatMessageDTO {
	id: string
	threadId: string
	actorType: SystemActorType
	actorId: string // user.id when actorType === "user"; AiActorTypeId when "ai"
	content: ChatMessageContent
	toolCallId: string | null
	createdAt: string // ISO-8601
}

export interface PostChatMessageBody {
	id?: string // client-allocated UUIDv4 (offline-first slice 4)
	content: string // 1..2000 chars, trimmed
	// Current-page context (ZPL-112): the route the user is on when sending.
	// `propertyId` is the thread's resource (path param) and is not duplicated
	// here; only `projectId` (set on a project page) travels in the body.
	context?: { projectId?: string }
	// Resolved @-mention picks (ZPL-145, shared chats). The server validates
	// each id is a participant of the thread, snapshots display names, and
	// drops anything invalid — it is authoritative.
	mentionUserIds?: string[]
	mentionsMareczek?: boolean
}

export interface PostChatMessageResponse {
	message: ChatMessageDTO
}

export interface GetChatMessagesQuery {
	limit?: number // 1..100, default 50
	before?: string // ISO timestamp; results where createdAt < before
}

export interface GetChatMessagesResponse {
	messages: ChatMessageDTO[] // DESC by createdAt
	nextBefore: string | null
}

export const CHAT_KINDS = ["private", "shared"] as const
export type ChatKind = (typeof CHAT_KINDS)[number]

export interface PropertyChatRefDTO {
	id: string
	kind: ChatKind
	unreadMessageCount: number
	/**
	 * Resource owning this chat. Older clients can keep using `kind`; newer
	 * property chat hubs use these fields to distinguish the property's group
	 * chat from project group chats that share the same `kind: "shared"`.
	 */
	resourceType?: "property" | "project"
	resourceId?: string
	/** Server-known display title for project chats; property chat titles stay client-localized. */
	title?: string
}

export interface PostChatAcknowledgeBody {
	messageId: string
}

export interface PostChatAcknowledgeResponse {
	unreadMessageCount: number
}
